import { Component, ComponentType, ReactNode } from 'react'
import { PasswordStrength } from 'interfaces/auth'
import * as styles from 'styles/atoms/inputs/PasswordField.css'
import { State } from 'interfaces/state'
import { connect } from 'react-redux'
import { TextFieldOwnProps } from 'components/atoms/inputs/TextField'
import * as capitalize from 'lodash/capitalize'

const STRENGTH_COLOR: Record<PasswordStrength, TextFieldOwnProps['color']> = {
  weak: 'red',
  medium: 'yellow',
  strong: 'green',
}

type WithPasswordStrengthStateProps = {
  strength: PasswordStrength | null
}

export type InjectedPasswordStrengthPropsInner = {
  strengthNode: ReactNode
  color?: TextFieldOwnProps['color']
}

export type InjectedPasswordStrengthProps = InjectedPasswordStrengthPropsInner & WithPasswordStrengthStateProps

const withPasswordStrength = <TProps extends InjectedPasswordStrengthPropsInner & WithPasswordStrengthStateProps>(WrappedComponent: ComponentType<TProps>) => {
  const wrappedComponentName = WrappedComponent.displayName
    || WrappedComponent.name
    || 'Component'

  const displayName = `PasswordStrength(${ wrappedComponentName })`

  class WrapperComponent extends Component<WithPasswordStrengthStateProps> {
    static displayName = displayName

    private get color () {
      const { strength } = this.props

      if (!strength) {
        return
      }

      const key = strength.toLowerCase() as PasswordStrength

      return STRENGTH_COLOR[key]
    }

    render () {
      const props: TProps = {
        ...this.props,
        strengthNode: this.renderPasswordStrength(),
        color: this.color,
      } as TProps

      return (
        <WrappedComponent { ...props } />
      )
    }

    renderPasswordStrength = () => {
      const { strength } = this.props

      if (!strength || !this.color) {
        return
      }

      return (
        <div className={ styles.messageWrapper }>
          Password Strength: <span className={ styles[this.color] }>{ capitalize(strength) }</span>
        </div>
      )
    }
  }

  const mapStateToProps = (state: State) => ({
    strength: state.app.auth.passwordStrength,
  })

  return connect<
    WithPasswordStrengthStateProps,
    {},
    TProps
    >(mapStateToProps)(WrapperComponent)
}

export default withPasswordStrength
